import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@toki/auth';
import { LIST_NARROW, useMediaQuery } from '../hooks/useMediaQuery';
import { usePermissions } from '../hooks/usePermissions';
import {
  adminCreateIncidencia,
  assignIncidencia,
  changePriority,
  changeStatus,
  createTicketMessage,
  deleteTicketFile,
  downloadTicketFile,
  markMessageResolutory,
  getIncidencia,
  getIncidenciaTimeline,
  listAllIncidencias,
  listAreas,
  listCategories,
  listInternalUsers,
  listWorklogs,
  lookupUserByEmail,
  type UserLookup,
  type Area,
  type Category,
  type Incidencia,
  type InternalUser,
  type TicketFile,
  esImagen,
  MAX_ADJUNTOS_PANEL,
  type TimelineEvent,
  type WorklogList,
} from '../services/api';
import {
  ArrowLeft,
  Printer,
  WifiHigh,
  Key,
  EnvelopeSimple,
  Desktop,
  AppWindow,
  ClipboardText,
  Warning,
  Lock,
  Browser,
  Door,
  Phone,
  User as UserIcon,
  ArrowBendUpLeft,
  type Icon,
} from '@phosphor-icons/react';
import {
  DEFAULT_PRIORITY,
  PRIORITY_HINTS,
  PRIORITY_LABELS,
  PRIORITY_OPTIONS,
  isNotablePriority,
  priorityLabel,
  priorityTone,
  type Priority,
} from '../config/priority';
import { getModuleColor } from '../config/modules';
import { getModuleIcon } from '../icons';
import { Topbar } from '../components/Topbar';
import { FaqPicker } from '../components/FaqPicker';
import { SiguientePaso } from '../components/SiguientePaso';
import { FilterBar } from '../components/FilterBar';
import filterBar from '../components/FilterBar.module.css';
import { PaneResizer } from '../components/PaneResizer';
import { ViewModeToggle, type PaneMode } from '../components/ViewModeToggle';
import { DetailSection } from '../components/DetailSection';
import { WorklogSection } from '../components/WorklogSection';
import { AdjuntosMensaje } from '../components/AdjuntosMensaje';
import { AdjuntosPendientes, BotonAdjuntar, capturasPegadas, sumarAdjuntos } from '../components/Adjuntar';
import { ListaAdjuntos, type OrigenAdjunto } from '../components/ListaAdjuntos';
import { VisorImagenes } from '../components/VisorImagenes';
import { useDialog } from '../components/useDialog';
import { parseDuration } from '../config/worklog';
import { duracion as slaDuracion, fechaCorta, slaVista } from '../config/sla';
import { PersonaModal } from '../components/PersonaModal';
import { Badge, type BadgeTone } from '../components/Badge';
import { type RowVisual, TILE_COLORS } from '../config/rowVisuals';
import { useResizablePane } from '../hooks/useResizablePane';
import styles from './Incidencias.module.css';

/** Dónde se abre el ticket: preferencia POR NAVEGADOR, igual que el colapso
 *  del raíl. Prefijo `kivai.` porque el portal comparte origen con el admin y
 *  las claves de `localStorage` son de los dos. */
const PANE_KEY = 'kivai.admin.incidencias.paneMode';

function readPaneMode(): PaneMode {
  try {
    const v = localStorage.getItem(PANE_KEY);
    return v === 'bottom' || v === 'off' ? v : 'right';
  } catch {
    // Ventana privada o almacenamiento bloqueado: la de siempre.
    return 'right';
  }
}

const mc = getModuleColor('incidencias');
const ModuleIcon = getModuleIcon('incidencias');



const STATUS_LABELS: Record<string, string> = {
  nueva: 'Nuevo',
  en_revision: 'En revisión',
  en_progreso: 'En progreso',
  resuelta: 'Resuelto',
  rechazada: 'Rechazado',
};

const STATUS_OPTIONS = ['nueva', 'en_revision', 'en_progreso', 'resuelta', 'rechazada'];

/**
 * Grafo de transiciones válidas — réplica en UX del que manda de verdad en
 * `incidencias/status_graph.py`. Esto NO es la barrera: es solo para no
 * ofrecer en el <select> una opción que el backend va a rechazar con 400.
 * `resuelta` y `rechazada` reabren a `en_progreso` (regla de reapertura,
 * paso 81), y se trata igual para las dos, como ya hace el email.
 */
const STATUS_TRANSITIONS: Record<string, string[]> = {
  nueva: ['en_revision', 'en_progreso', 'rechazada'],
  en_revision: ['nueva', 'en_progreso', 'rechazada'],
  en_progreso: ['en_revision', 'resuelta', 'rechazada'],
  resuelta: ['en_progreso'],
  rechazada: ['en_progreso'],
};

/** Los estados que CIERRAN un ticket (mismo par que `reopen.CLOSED_STATUSES`). */
const CLOSED_STATUSES = ['resuelta', 'rechazada'];

function statusOptionsFrom(current: string): string[] {
  const allowed = STATUS_TRANSITIONS[current] ?? [];
  return [current, ...allowed.filter((s) => s !== current)];
}

/**
 * Canal por el que entró el ticket (`source` del backend, schemas.py:17). Son
 * CUATRO y estos cuatro: el prototipo del rediseño inventaba un «Chat» que no
 * existe, y `app` no aparecía por ninguna parte.
 *
 * `app` se etiqueta «Web» y no «App»: hoy la única interfaz propia es el admin
 * en el navegador, y la app móvil se descartó a propósito (PLAN.md §4).
 *
 * ⚠️ «Correo» es a la vez CANAL y CATEGORÍA del seed, así que pintar los dos
 * como texto en la misma línea produce `Correo · Correo`. Por eso el canal se
 * enseña con ICONO + `title` y la categoría con palabra: son dos ejes
 * distintos y no deben competir por el mismo renglón. Ver paso 33 §5.6.
 */
const SOURCE_LABELS: Record<string, string> = {
  app: 'Web',
  // Abierto por el cliente desde el portal (paso 96b). Antes quedaba como
  // `app` y no se distinguía de un alta de staff; los tickets anteriores al
  // 96b siguen diciendo «Web».
  portal: 'Portal',
  phone: 'Teléfono',
  email: 'Correo',
  in_person: 'Presencial',
};

const SOURCE_ICONS: Record<string, Icon> = {
  app: Browser,
  portal: Door,
  phone: Phone,
  email: EnvelopeSimple,
  in_person: UserIcon,
};

/** Canales que puede elegir quien da de alta un ticket A MANO. `app` no está:
 *  un ticket tecleado en el panel no ha entrado por la web del usuario. */
const MANUAL_SOURCES = ['phone', 'email', 'in_person'] as const;

/** Punto del historial. Mismos tonos que el badge del estado correspondiente.
 *
 *  ⚠️ Apunta a los TOKENS, no a hex copiados. Hasta el paso 36 esto llevaba
 *  los valores escritos (`#136A73`, `#4F3FBF`, `#6B6055`…) y era una copia más
 *  de la paleta de estados, en un `.tsx`, donde no la encuentra quien busca
 *  colores en los `.css`: al cambiar la paleta, estos puntos se quedaron con
 *  los tonos viejos y nada falló. Si hace falta un estado nuevo, el token va
 *  primero en `admin-tokens.css`. */
const STATUS_COLORS: Record<string, string> = {
  nueva: 'var(--st-nuevo-fg)',
  en_revision: 'var(--st-revision-fg)',
  en_progreso: 'var(--st-progreso-fg)',
  resuelta: 'var(--st-resuelto-fg)',
  rechazada: 'var(--st-rechazado-fg)',
};

/**
 * Tono del badge por estado.
 *
 * Ninguno es 'blue': el azul significa "se puede pulsar", y un estado no se
 * pulsa. 'nueva' pasa de azul a teal por eso.
 *
 * 'rechazada' pasa de rojo a gris cálido: el rojo queda reservado para "actúa
 * ahora" (fuera de SLA, error). Un ticket rechazado está cerrado y tranquilo,
 * no es una alarma — teñirlo de rojo gastaba el color más caro de la paleta en
 * el estado que menos atención necesita.
 */
const STATUS_TONE: Record<string, BadgeTone> = {
  nueva: 'teal',
  en_revision: 'purple',
  en_progreso: 'amber',
  resuelta: 'green',
  rechazada: 'slate',
};

/**
 * Tile visual (icono Phosphor + color suave) por categoría de ticket.
 *
 * Estas reglas eran las del portal municipal de Toki (alumbrado, basura,
 * jardín, tráfico…) y NO casaban con ninguna categoría IT de Kivai, así que
 * todos los tickets caían al tile por defecto y los diez colores no se veían
 * nunca. Aquí van las categorías reales del seed.
 *
 * Es el espejo en la UI de CATEGORY_RULES del backend
 * (app/incidencias/intake.py), que clasifica los correos entrantes. Si se
 * añade una categoría, toca los dos sitios.
 */
const CATEGORY_VISUAL_RULES: { match: RegExp; visual: RowVisual }[] = [
  { match: /impres|imprim|toner|cartucho|escane|fotocopi/, visual: { Icon: Printer, ...TILE_COLORS.purple } },
  { match: /red|conectivid|vpn|wifi|wi-fi|internet|router|conexion/, visual: { Icon: WifiHigh, ...TILE_COLORS.sky } },
  { match: /acceso|cuenta|contrase|password|permiso|credencial|usuario/, visual: { Icon: Key, ...TILE_COLORS.amber } },
  { match: /correo|email|mail|outlook|buzon/, visual: { Icon: EnvelopeSimple, ...TILE_COLORS.teal } },
  { match: /hardware|equipo|portatil|ordenador|monitor|pantalla|teclado|raton/, visual: { Icon: Desktop, ...TILE_COLORS.blue } },
  { match: /software|programa|aplicaci|licencia|instalaci/, visual: { Icon: AppWindow, ...TILE_COLORS.indigo } },
  { match: /solicitud|peticion|alta|baja/, visual: { Icon: ClipboardText, ...TILE_COLORS.green } },
];

const CATEGORY_DEFAULT_VISUAL: RowVisual = { Icon: Warning, ...TILE_COLORS.slate };

function categoryVisual(category: string): RowVisual {
  const cat = (category ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
  for (const rule of CATEGORY_VISUAL_RULES) {
    if (rule.match.test(cat)) return rule.visual;
  }
  return CATEGORY_DEFAULT_VISUAL;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * «Abierto hace»: cuánto lleva vivo el ticket.
 *
 * Desde el paso 89 va DEBAJO del SLA, en la misma celda. No son lo mismo: el
 * SLA dice *cuánto te queda* para la primera respuesta contra el plazo del
 * pack (`config/sla.ts`); esto dice *cuánto lleva esperando*, que es un hecho
 * medible y sigue ahí cuando la primera respuesta ya se dio.
 *
 * Por eso esto NO se colorea: el color es del SLA, que sí tiene un plazo
 * detrás. Pintarlo también sería una segunda alarma diciendo otra cosa.
 */
function formatElapsed(iso: string, now: number): string {
  const mins = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  if (days < 7) return rest > 0 ? `${days} d ${rest} h` : `${days} d`;
  return `${days} d`;
}

/** Iniciales para el avatar del hilo y de la columna Técnico. Dos como mucho:
 *  «Marta Ruiz» → MR, «Soporte» → S, vacío → «—». */
function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  return parts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
}

/**
 * Orden de la bandeja: PRIORIDAD primero, ANTIGÜEDAD después.
 *
 * Hasta ahora la lista salía como la devolviera la API, o sea sin criterio.
 * Se descartaron dos alternativas (paso 33 §5.2): ordenar solo por antigüedad
 * deja un `crítica` de esta mañana por debajo de un `baja` de la semana
 * pasada; y ordenar por última actividad —como una bandeja de correo— hunde
 * justo el ticket que nadie ha tocado, que es el que hay que ver.
 */
const PRIORITY_RANK: Record<string, number> = { critica: 0, alta: 1, media: 2, baja: 3 };

function byPriorityThenAge(a: Incidencia, b: Incidencia): number {
  const pa = PRIORITY_RANK[a.priority] ?? 2;
  const pb = PRIORITY_RANK[b.priority] ?? 2;
  if (pa !== pb) return pa - pb;
  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
}

/**
 * Orden ELEGIBLE de la lista (paso 43, el «Más antiguos ▾» del v3).
 *
 * Es de PRESENTACIÓN, como los filtros: reordena en el cliente las filas que
 * la API ya decidió devolver y no toca qué ve cada rol. El de siempre sigue
 * siendo el de por defecto — ver arriba por qué prioridad y luego antigüedad.
 * Preferencia por navegador, igual que la vista del panel.
 */
type SortMode = 'prioridad' | 'antiguos' | 'recientes';
const SORT_KEY = 'kivai.admin.incidencias.sort';
const SORT_LABELS: Record<SortMode, string> = {
  prioridad: 'Prioridad',
  antiguos: 'Más antiguos',
  recientes: 'Más recientes',
};
const SORTERS: Record<SortMode, (a: Incidencia, b: Incidencia) => number> = {
  prioridad: byPriorityThenAge,
  antiguos: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  recientes: (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
};

function readSortMode(): SortMode {
  try {
    const v = localStorage.getItem(SORT_KEY);
    return v === 'antiguos' || v === 'recientes' ? v : 'prioridad';
  } catch {
    return 'prioridad';
  }
}

/** Rótulo de la cabecera de la lista según la pestaña: es la pregunta que
 *  responde la lista que tienes delante («Mis tickets» en el v3). */
const LIST_TITLES: Record<'' | 'unassigned' | 'mine', string> = {
  '': 'Todos los tickets',
  unassigned: 'Tickets sin asignar',
  mine: 'Mis tickets',
};

export function IncidenciasScreen() {
  // La bandeja tiene DOS formas y son la misma lista: tabla de columnas
  // cuando hay sitio, y filas apiladas cuando no. «No hay sitio» son dos
  // cosas distintas que producen el mismo modo: que haya un ticket abierto
  // (el panel se estrecha a ~320px) o que la ventana sea estrecha de por sí.
  // Las dos ponen `data-compact`, así que hay UN solo juego de reglas.
  const narrow = useMediaQuery(LIST_NARROW);
  const { state: authState } = useAuth();
  const { hasPermission } = usePermissions();
  const token = authState.token?.accessToken ?? '';
  const user = authState.user;

  // Role-based view mode
  const role = user?.role ?? 'requester';
  const userAreas = user?.areas ?? [];
  const isAdmin = role === 'admin' || role === 'superadmin';
  const isCoordinator = role === 'coordinator';
  const isAgent = role === 'agent';

  const [items, setItems] = useState<Incidencia[]>([]);
  const [selected, setSelected] = useState<Incidencia | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [search, setSearch] = useState('');
  /**
   * Vista de trabajo, que NO es lo mismo que un filtro de atributo. «Sin
   * asignar» y «Míos» son las dos preguntas que un técnico se hace al abrir
   * la bandeja; el estado y la prioridad son atributos y viven en sus
   * desplegables. El prototipo traía además «Vencen hoy», que se cae con el
   * SLA — no hay vencimiento que mirar.
   *
   * ⚠️ Estos filtros son de PRESENTACIÓN, no de seguridad: recortan en el
   * cliente lo que la API ya decidió devolver. Quien decide qué filas ve cada
   * rol es `incidencias/router.py`, y sigue haciéndolo igual.
   */
  const [viewTab, setViewTab] = useState<'' | 'unassigned' | 'mine'>('');
  const [sortMode, setSortMode] = useState<SortMode>(readSortMode);
  const changeSortMode = (m: SortMode) => {
    setSortMode(m);
    try {
      localStorage.setItem(SORT_KEY, m);
    } catch {
      // Sin almacenamiento dura lo que la pestaña.
    }
  };
  /** Momento de render: se congela para que las antigüedades de toda la lista
   *  se midan contra el MISMO instante y la columna no baile entre filas. */
  const [now, setNow] = useState(() => Date.now());
  // El SLA del ticket abierto (paso 89): el mismo cálculo que la fila.
  const selectedSla = selected ? slaVista(selected, now) : null;
  const [loading, setLoading] = useState(true);

  // Detail panel state
  /** La captura abierta en la caja de luz: índice en `imagenes`, o null. */
  const [visor, setVisor] = useState<number | null>(null);
  const [fileBusy, setFileBusy] = useState(false);
  const [resolutoryBusy, setResolutoryBusy] = useState(false);
  const { confirm, element: dialog } = useDialog();
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [draft, setDraft] = useState('');
  // 'internal' por defecto: si alguien escribe y envía sin fijarse, lo peor que
  // pasa es una nota que nadie ve — nunca una nota interna publicada.
  const [draftVisibility, setDraftVisibility] = useState<'public' | 'internal'>('internal');
  const [draftResolutory, setDraftResolutory] = useState(false);
  /** Tiempo que se apunta CON el mensaje (paso 93): texto libre, «25», «1h30»… */
  const [draftMinutes, setDraftMinutes] = useState('');
  /** Adjuntos que salen CON el mensaje (paso 34): no se suben hasta enviar. */
  const [draftFiles, setDraftFiles] = useState<File[]>([]);
  const [draftFilesAviso, setDraftFilesAviso] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  /** Tiempo dedicado del ticket abierto. `null` mientras carga o si falla. */
  const [worklogs, setWorklogs] = useState<WorklogList | null>(null);

  // Reference data
  const [categories, setCategories] = useState<Category[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [staffUsers, setStaffUsers] = useState<InternalUser[]>([]);
  const [assignAreaId, setAssignAreaId] = useState<string>('');
  const [assignToId, setAssignToId] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('');
  /* Cliente (85b). Como los demás, de PRESENTACIÓN: recorta lo que la API ya
     devolvió. Las opciones salen de los tickets cargados y no de
     `/admin/clientes`: así no hay una petición más, y no se ofrece un cliente
     que en esta bandeja daría cero filas. `__none__` = sin cliente. */
  const [clienteFilter, setClienteFilter] = useState<string>('');
  /** Contador que fuerza a ABRIR la tarjeta «Gestión». Lo sube el botón
   *  «Resolver» de la cabecera, que es un atajo a esa misma tarjeta: sin esto,
   *  pulsarlo con la tarjeta plegada no enseñaba ni el aviso de respuesta
   *  resolutoria ni el botón que remata el cambio. */
  const [openGestion, setOpenGestion] = useState(0);
  /** Colapso del hilo: de serie solo se ven las últimas entradas. Un ticket de
   *  soporte con quince idas y venidas empieza por «buenos días» y lo que hace
   *  falta está al final. El prototipo lo resuelve igual («2 mensajes
   *  anteriores · Ver todo») y es de sus mejores ideas. */
  const [threadExpanded, setThreadExpanded] = useState(false);

  /* Dos permisos distintos, no uno con matices (decisión de Jose, 2026-09-25):
     · `incidencias:assign`      — admin y coordinador: asignan a quien sea.
     · `incidencias:assign_self` — el TÉCNICO: puede cogerse el ticket y puede
       pasarlo a otro equipo, pero no ponérselo a otra persona.
     ⚠️ Esto es PRESENTACIÓN. Quien impone el techo de verdad es el backend, en
     `assign_incidencia`, y está fijado con `tests/test_assign_self.py`. Aquí
     solo se evita ofrecer botones que la API va a rechazar. */
  const canAssign = hasPermission('incidencias:assign');
  const canAssignSelf = hasPermission('incidencias:assign_self');
  /* Abrir un ticket es su propio permiso desde el 2026-09-26. Antes colgaba de
     `assign`, así que el botón «+ Nuevo ticket» no le salía al técnico — que es
     justo quien coge el teléfono. */
  const canCreate = hasPermission('incidencias:create');
  const canVerFaqs = hasPermission('asistente_portal:view');
  const canVerTodo = hasPermission('incidencias:view_all');
  /* Dar de alta un cliente es su propio permiso (`users:create_requester`) y
     no `users:manage`: un técnico necesita crear la cuenta del cliente al que
     le está abriendo el ticket —pasa a menudo— y eso no tiene por qué llevar
     consigo editar y desactivar al resto de usuarios, internos incluidos.
     `users:manage` lo sigue implicando, así que el admin no pierde nada. */
  const canCreateRequester = hasPermission('users:create_requester')
    || hasPermission('users:manage');
  const canTouchAssignment = canAssign || canAssignSelf;
  const canUpdateStatus = hasPermission('incidencias:update_status');
  const canReply = hasPermission('incidencias:reply');
  /* Paso 93: apuntar tiempo. Corregir el de OTRO pide además `assign`, pero
     eso lo dice el backend apunte a apunte (`canEdit`). */
  const canLogTime = hasPermission('incidencias:log_time');
  /* K53: el ticket de un cliente DESACTIVADO está congelado — solo se puede
     cerrar (a `rechazada` desde cualquier estado abierto, o a `resuelta` si el
     grafo lo permite). La regla la impone el backend (409 `TICKET_FROZEN`);
     esto solo evita ofrecer controles que van a fallar. Se calcula sobre el
     ticket abierto, no es un permiso. */
  const frozen = selected?.frozen === true;

  // Create form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createTitle, setCreateTitle] = useState('');
  const [createDesc, setCreateDesc] = useState('');
  const [createCategory, setCreateCategory] = useState('');
  const [createPriority, setCreatePriority] = useState<Priority>(DEFAULT_PRIORITY);
  const [createSource, setCreateSource] = useState<'phone' | 'email' | 'in_person'>('phone');
  const [createReporterName, setCreateReporterName] = useState('');
  const [createReporterPhone, setCreateReporterPhone] = useState('');
  const [createReporterEmail, setCreateReporterEmail] = useState('');
  const [createAddress, setCreateAddress] = useState('');
  const [createAreaId, setCreateAreaId] = useState('');
  const [createAssignTo, setCreateAssignTo] = useState('');
  const [createFiles, setCreateFiles] = useState<File[]>([]);
  const [createFilesAviso, setCreateFilesAviso] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  /* Comprobación de FORMA — deliberadamente floja: basta con «algo@algo.algo».
     Es el filtro previo: sin esto se consultaría al servidor por cada tecla. */
  const emailMalFormado = createReporterEmail.trim().length > 0
    && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(createReporterEmail.trim());

  /* ── ¿Esa dirección tiene cuenta? ─────────────────────────────────
     Lo que hay detrás de esta pregunta: el alta guarda `reporterEmail` como
     texto y el ticket nace HUÉRFANO (`user_id = NULL`). Quien lo ata a una
     persona es la adopción del portal, y casa por ese correo. Sin cuenta, el
     ticket existe y **nadie lo ve desde el portal** — sin error y sin aviso.
     Por eso se pregunta al escribirlo y no se descubre tres semanas después. */
  const [emailLookup, setEmailLookup] = useState<UserLookup | null>(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [personaOpen, setPersonaOpen] = useState(false);

  useEffect(() => {
    const email = createReporterEmail.trim();
    if (!showCreateForm || !token || emailMalFormado || email.length === 0) {
      setEmailLookup(null);
      return;
    }
    // 500ms de espera: se escribe una dirección de un tirón y no tiene sentido
    // preguntar por cada letra. Y `cancelado` evita que una respuesta lenta de
    // un correo ya borrado pise a la del que hay escrito ahora.
    let cancelado = false;
    setEmailChecking(true);
    const id = setTimeout(() => {
      lookupUserByEmail(email, token)
        .then((r) => { if (!cancelado) setEmailLookup(r); })
        .catch(() => { if (!cancelado) setEmailLookup(null); })
        .finally(() => { if (!cancelado) setEmailChecking(false); });
    }, 500);
    return () => { cancelado = true; clearTimeout(id); setEmailChecking(false); clearTimeout(id); };
  }, [createReporterEmail, showCreateForm, token, emailMalFormado]);

  /* Paso 85a (D4): «Crear cuenta» abre el formulario ÚNICO de persona, con el
     correo y el nombre ya escritos y el selector de cliente. Hasta el 85a este
     botón llamaba a `createRequester` por su cuenta: era un segundo camino de
     alta, y con el cliente obligatorio se habría quedado en un 422. */
  const handleCreateAccount = () => {
    if (!createReporterEmail.trim()) return;
    setMessage(null);
    setPersonaOpen(true);
  };

  /* ── Dónde se abre el ticket ────────────────────────────────────
     Tres posiciones, las del panel de lectura de Outlook. Ver
     `components/ViewModeToggle.tsx` para qué significa cada una.

     ⚠️ `paneMode` es lo que ha PEDIDO la persona y `effectivePane` lo que
     cabe en la ventana: por debajo de 900px no hay sitio para dos paneles se
     pida lo que se pida, así que manda `off`. Se guardan aparte por la misma
     razón que el raíl (`App.tsx`): al ensanchar el navegador tiene que volver
     la vista que esa persona eligió, no la que le impuso el tamaño. */
  const [paneMode, setPaneMode] = useState<PaneMode>(readPaneMode);
  const effectivePane: PaneMode = narrow ? 'off' : paneMode;

  const changePaneMode = (m: PaneMode) => {
    setPaneMode(m);
    try {
      localStorage.setItem(PANE_KEY, m);
    } catch {
      // Sin almacenamiento la preferencia dura lo que la pestaña. Aceptable.
    }
  };

  /* ⚠️ «Hay algo en el panel de la derecha» es UNA condición y se calcula UNA
     vez: un ticket abierto o el formulario de alta. Se separaron durante media
     hora —el layout miraba las dos y la lista solo `selected`— y el resultado
     fue que con el alta abierta la lista se quedaba en 320px pero seguía
     pintando sus seis columnas: la cabecera imprimía «ASUNTO» encima de
     «USUARIO» y los títulos desaparecían. El mismo patrón que el
     `grid-template-areas` del modo compacto: dos sitios que describen la misma
     forma y solo se corrige uno. */
  const splitOpen = Boolean(selected) || showCreateForm;

  /* La lista se encoge —y pasa a tarjetas— SOLO cuando comparte el ancho con
     el panel de la derecha. En «debajo» y en «sin vista previa» ocupa toda la
     ventana, así que mantiene sus columnas. */
  const listCompact = narrow || (effectivePane === 'right' && splitOpen);

  /* Si la lista está en pantalla. Solo deja de estarlo en «sin vista previa»
     con algo abierto, que es cuando el detalle ocupa la ventana entera. */
  const listVisible = !(effectivePane === 'off' && splitOpen);

  /* Dos tiradores, uno por eje, con CLAVES DISTINTAS: el rango de un ancho
     (240–600) y el de un alto (160–700) no se parecen, y compartir clave haría
     que cambiar de vista arrastrara un tamaño que la otra descarta. */
  const { size: listPaneWidth, resizerProps } = useResizablePane('incidencias:listPane', { axis: 'x' });
  const { size: listPaneHeight, resizerProps: resizerVProps } = useResizablePane(
    'incidencias:listPaneH',
    { axis: 'y', defaultSize: 300, min: 160, max: 700 },
  );

  // Load areas and staff users for assignment dropdowns
  useEffect(() => {
    if (!token) return;
    listCategories(token).then(setCategories).catch(() => {});
    /* ⚠️ Los EQUIPOS se cargan para cualquiera que pueda tocar la asignación,
       porque pasar el ticket a otro equipo es justo lo que `assign_self`
       permite. Las PERSONAS no: ese endpoint pide `users:view`, que un técnico
       no tiene — y no le hace falta, porque la única persona a la que puede
       asignar es él mismo y ya la tenemos en `user`. */
    if (!canTouchAssignment) return;
    listAreas(token).then((a) => setAreas(a.filter((x) => x.isActive))).catch(() => {});
    if (!canAssign) return;
    listInternalUsers(token)
      .then((u) => setStaffUsers(u.filter((x) => x.isActive)))
      .catch(() => {});
  }, [token, canAssign, canTouchAssignment]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      // Build filters based on role
      let effectiveAreaId = areaFilter || undefined;
      let effectiveAssignedTo: string | undefined;

      if ((isCoordinator || isAgent) && userAreas.length > 0 && !areaFilter) {
        // Coordinator & agent: default to first area
        effectiveAreaId = userAreas[0]?.id;
      }

      const result = await listAllIncidencias(token, {
        status: statusFilter || undefined,
        assignedAreaId: effectiveAreaId,
        assignedTo: effectiveAssignedTo,
        limit: 100,
      });
      setItems(result.items);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter, areaFilter, isCoordinator, isAgent, user?.id, userAreas]);

  useEffect(() => {
    if (token) fetchList();
  }, [token, fetchList]);

  // «Abierto hace» envejece solo. Un minuto es la resolución de la columna, así
  // que refrescar más a menudo no cambiaría ni un píxel.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const loadTimeline = useCallback(async (id: string) => {
    try {
      const events = await getIncidenciaTimeline(id, token);
      setTimeline(events);
    } catch {
      setTimeline([]);
    }
  }, [token]);

  const loadWorklogs = useCallback(async (id: string) => {
    try {
      setWorklogs(await listWorklogs(id, token));
    } catch {
      setWorklogs(null);
    }
  }, [token]);

  const handleSelect = (item: Incidencia) => {
    setSelected(item);
    setNewStatus(item.status);
    setStatusNote('');
    setAssignAreaId(item.assignedAreaId ?? '');
    setAssignToId(item.assignedTo ?? '');
    setMessage(null);
    setVisor(null);
    setDraft('');
    setDraftFiles([]);
    setDraftFilesAviso(null);
    setDraftVisibility('internal');
    setDraftResolutory(false);
    setDraftMinutes('');
    setThreadExpanded(false);
    setWorklogs(null);
    loadTimeline(item.id);
    loadWorklogs(item.id);
  };

  const handleSendMessage = async () => {
    const body = draft.trim();
    // Solo el fichero también vale: «aquí va el log» y enviar. El texto lo pone
    // el backend, el mismo que el relay.
    if (!selected || (!body && draftFiles.length === 0)) return;
    let minutes: number | undefined;
    if (draftMinutes.trim()) {
      const parsed = parseDuration(draftMinutes);
      if (parsed === null) {
        setMessage('No entiendo ese tiempo. Escribe, por ejemplo, «25», «1 h» o «1h30».');
        return;
      }
      minutes = parsed;
    }
    setSending(true);
    try {
      // Mensaje, adjuntos y tiempo en UNA llamada: o entran todos o ninguno.
      const conAdjuntos = draftFiles.length > 0;
      await createTicketMessage(
        selected.id, body, token, draftVisibility, draftResolutory, minutes, draftFiles,
      );
      setDraft('');
      setDraftResolutory(false);
      setDraftMinutes('');
      setDraftFiles([]);
      setDraftFilesAviso(null);
      loadTimeline(selected.id);
      if (minutes) loadWorklogs(selected.id);
      // Los adjuntos viven en el ticket (`files`), no en el timeline: sin
      // releerlo, el mensaje recién enviado saldría sin ellos.
      if (conAdjuntos) {
        const id = selected.id;
        getIncidencia(id, token)
          .then((fresh) => setSelected((cur) => (cur?.id === id ? fresh : cur)))
          .catch(() => {});
      }
      // La PRIMERA respuesta pública para el reloj del SLA (paso 89): sin
      // releer el ticket, la fila y el detalle seguirían con la cuenta atrás
      // hasta recargar. Una nota interna no cuenta, y ya respondido no cambia.
      if (draftVisibility === 'public' && selected.firstResponseDueAt && !selected.firstResponseAt) {
        const id = selected.id;
        getIncidencia(id, token)
          .then((fresh) => {
            setSelected((cur) => (cur?.id === id ? fresh : cur));
            setItems((cur) => cur.map((i) => (i.id === id ? fresh : i)));
          })
          .catch(() => {});
      }
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'No se pudo enviar el mensaje');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async () => {
    if (!selected || !newStatus) return;
    setSaving(true);
    try {
      const updated = await changeStatus(selected.id, newStatus, statusNote || null, token);
      setSelected(updated);
      setStatusNote('');
      setMessage('Estado actualizado');
      fetchList();
      loadTimeline(selected.id);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * La prioridad se guarda AL CAMBIARLA, sin pasar por el botón «Actualizar».
   * Ese botón manda el estado junto con su nota, y colgar de él un segundo
   * campo haría que tocar la prioridad empujara también un cambio de estado que
   * nadie pidió. Es un campo suelto y corregible: guardar en el acto es lo
   * honesto.
   */
  const handlePriorityChange = async (priority: string) => {
    if (!selected || priority === selected.priority) return;
    setSaving(true);
    try {
      const updated = await changePriority(selected.id, priority, token);
      setSelected(updated);
      setMessage(`Prioridad: ${priorityLabel(priority)}`);
      fetchList();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await assignIncidencia(
        selected.id,
        assignToId || null,
        assignAreaId || null,
        token,
      );
      setSelected(updated);
      setMessage('Asignación actualizada');
      fetchList();
      loadTimeline(selected.id);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleResolutory = async (messageId: string, resolutory: boolean) => {
    if (!selected) return;
    setResolutoryBusy(true);
    try {
      await markMessageResolutory(selected.id, messageId, resolutory, token);
      loadTimeline(selected.id);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'No se pudo marcar la respuesta');
    } finally {
      setResolutoryBusy(false);
    }
  };

  const handleDownloadFile = async (file: TicketFile) => {
    if (!selected) return;
    try {
      await downloadTicketFile(selected.id, file, token);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'No se pudo descargar el adjunto');
    }
  };

  const handleDeleteFile = async (file: TicketFile) => {
    if (!selected) return;
    // Borrar es para siempre (queda en la auditoría, no en el ticket): se
    // pregunta. La galería y los chips de antes borraban a la primera.
    const ok = await confirm({
      title: 'Borrar adjunto',
      message: `Se borrará «${file.filename}» del ticket. No se puede deshacer.`,
      confirmLabel: 'Borrar',
      danger: true,
    });
    if (!ok) return;
    const fileId = file.id;
    setVisor(null);
    setFileBusy(true);
    try {
      await deleteTicketFile(selected.id, fileId, token);
      setSelected((prev) =>
        prev ? { ...prev, files: (prev.files ?? []).filter((f) => f.id !== fileId) } : prev,
      );
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'No se pudo borrar el adjunto');
    } finally {
      setFileBusy(false);
    }
  };

  const handleCreate = async () => {
    if (!createTitle.trim() || !createDesc.trim() || !createCategory) return;
    setCreating(true);
    try {
      await adminCreateIncidencia({
        title: createTitle.trim(),
        description: createDesc.trim(),
        category: createCategory,
        priority: createPriority,
        source: createSource,
        reporterName: createReporterName.trim() || undefined,
        reporterPhone: createReporterPhone.trim() || undefined,
        reporterEmail: createReporterEmail.trim() || undefined,
        address: createAddress.trim() || undefined,
        assignedAreaId: createAreaId || undefined,
        assignedTo: createAssignTo || undefined,
      }, token, createFiles);
      setShowCreateForm(false);
      setCreateFiles([]); setCreateFilesAviso(null);
      setCreateTitle(''); setCreateDesc(''); setCreateCategory(''); setCreatePriority(DEFAULT_PRIORITY);
      setCreateSource('phone'); setCreateReporterName(''); setCreateReporterPhone(''); setCreateReporterEmail('');
      setCreateAddress(''); setCreateAreaId(''); setCreateAssignTo('');
      setMessage('Ticket creado');
      fetchList();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Error al crear');
    } finally {
      setCreating(false);
    }
  };

  const createFilteredStaff = createAreaId
    ? staffUsers.filter((u) => u.areas.some((a) => a.id === createAreaId))
    : staffUsers;

  // Filter staff users by selected area
  /* Con el permiso completo, la plantilla filtrada por equipo. Con `assign_self`
     la lista es UNA persona —tú—, construida del usuario en sesión y no de la
     API, que ahí devolvería 403. */
  const filteredStaff = canAssign
    ? (assignAreaId
        ? staffUsers.filter((u) => u.areas.some((a) => a.id === assignAreaId))
        : staffUsers)
    : user
      ? [{
          id: user.id,
          displayName: user.displayName ?? user.email,
          email: user.email,
          roleId: '',
          roleName: 'tú',
          areas: [],
          isActive: true,
          openTickets: 0,
          createdAt: '',
        } satisfies InternalUser]
      : [];

  /* El ticket ya lo lleva OTRA persona y tú solo tienes `assign_self`: el
     backend rechazará cualquier cambio de asignado, así que el desplegable se
     deshabilita en vez de ofrecer una acción que va a dar 403. El equipo sí se
     puede seguir cambiando. */
  const assignLocked = !canAssign
    && Boolean(selected?.assignedTo)
    && selected?.assignedTo !== user?.id;


  /**
   * Equipos que puede elegir el desplegable. Admin ve todos; coordinador y
   * técnico, solo los suyos — y solo si tienen más de uno, porque con uno el
   * desplegable no ofrece ninguna elección y es ruido.
   */
  const areaOptions = isAdmin ? areas : userAreas.length > 1 ? userAreas : [];

  /**
   * «Sin asignar» = **sin TÉCNICO**, no sin equipo.
   *
   * Antes eran dos definiciones distintas para lo mismo y se contradecían en
   * pantalla: el contador exigía `!assignedTo && !assignedAreaId && status
   * === 'nueva'` y la columna Técnico solo miraba `assigneeName`. Con el
   * enrutado automático por categoría —que asigna EQUIPO pero no persona— el
   * resultado era una pestaña «Sin asignar 0» encima de seis filas que decían
   * «Sin asignar». Un contador que desmiente a la lista que tiene debajo no es
   * un detalle estético: es la razón por la que nadie vuelve a fiarse de él.
   *
   * Se elige «sin técnico» y no «sin equipo» porque es lo accionable: un
   * ticket con equipo y sin persona no lo está mirando nadie.
   */
  const isUnassigned = (i: Incidencia) => !i.assignedTo;
  const unassignedCount = items.filter(isUnassigned).length;
  const isMine = (i: Incidencia) => Boolean(user?.id) && i.assignedTo === user?.id;

  const viewTabs = [
    { label: 'Todos', value: '', count: items.length },
    { label: 'Sin asignar', value: 'unassigned', count: items.filter(isUnassigned).length },
    { label: 'Míos', value: 'mine', count: items.filter(isMine).length },
  ];

  /**
   * «Carga del equipo» — el bloque de barras del aside del documento de
   * diseño. Es el ÚNICO de ese aside que se puede pintar hoy: sale de los
   * tickets ya cargados y de `assigneeName`, sin entidad nueva ni endpoint
   * nuevo. El otro bloque del documento, «Vistas guardadas», es C2/90 y no
   * está aquí — ver K29.
   *
   * ⚠️ Cuenta sobre `items`, o sea sobre la PÁGINA cargada (hasta 100), no
   * sobre toda la base. Es lo mismo que hacen las pestañas y el buscador, y
   * por eso el rótulo dice «de los tickets cargados»: una barra que se
   * presentara como la carga real del equipo estaría mintiendo en cuanto
   * hubiera 101 tickets.
   *
   * Abierto = ni resuelta ni rechazada. Y «Sin asignar» reutiliza
   * `isUnassigned` a propósito: es la tercera vez que ese concepto aparece
   * en la pantalla y ya se contradijo una vez consigo mismo.
   */
  const OPEN_STATES = (i: Incidencia) => i.status !== 'resuelta' && i.status !== 'rechazada';
  const workload = (() => {
    const open = items.filter(OPEN_STATES);
    const byAgent = new Map<string, number>();
    for (const i of open) {
      if (isUnassigned(i)) continue;
      const name = i.assigneeName ?? 'Sin nombre';
      byAgent.set(name, (byAgent.get(name) ?? 0) + 1);
    }
    const rows = [...byAgent.entries()]
      .map(([name, count]) => ({ name, count, unassigned: false }))
      .sort((a, b) => b.count - a.count);
    const none = open.filter(isUnassigned).length;
    // «Sin asignar» va SIEMPRE al final y aunque sea cero: es la barra que se
    // mira, y desaparecer cuando vale cero le quita justo la lectura de «hoy
    // no hay nada sin coger».
    rows.push({ name: 'Sin asignar', count: none, unassigned: true });
    const max = Math.max(1, ...rows.map((r) => r.count));
    return rows.map((r) => ({ ...r, pct: Math.round((r.count / max) * 100) }));
  })();

  // Los clientes de los tickets cargados, para el desplegable (85b).
  const clienteOptions = Array.from(
    new Map(
      items.filter((i) => i.clienteId).map((i) => [i.clienteId as string, i.clienteNombre ?? '—']),
    ),
  ).sort((a, b) => a[1].localeCompare(b[1], 'es'));
  const sinClienteCount = items.filter((i) => !i.clienteId).length;

  const normalizedSearch = search.trim().toLowerCase();
  const filteredItems = items.filter((i) => {
    if (viewTab === 'unassigned' && !isUnassigned(i)) return false;
    if (viewTab === 'mine' && !isMine(i)) return false;
    if (statusFilter && i.status !== statusFilter) return false;
    if (priorityFilter && i.priority !== priorityFilter) return false;
    if (assigneeFilter === '__none__' && i.assignedTo) return false;
    if (assigneeFilter && assigneeFilter !== '__none__' && i.assignedTo !== assigneeFilter) return false;
    if (clienteFilter === '__none__' && i.clienteId) return false;
    if (clienteFilter && clienteFilter !== '__none__' && i.clienteId !== clienteFilter) return false;
    if (
      normalizedSearch
      && !i.title.toLowerCase().includes(normalizedSearch)
      && !i.category.toLowerCase().includes(normalizedSearch)
      // La referencia entera (kv-2608-4h7q) y la cola suelta (4h7q), que es
      // como la cita el usuario por teléfono: `includes` cubre las dos.
      && !i.reference.toLowerCase().includes(normalizedSearch)
      && !(i.reporterName ?? '').toLowerCase().includes(normalizedSearch)
      && !(i.clienteNombre ?? '').toLowerCase().includes(normalizedSearch)
    ) return false;
    return true;
  }).sort(SORTERS[sortMode]);



  // ── Hilo: qué entradas se ven ─────────────────────────────────────────
  // Se enseñan las últimas THREAD_TAIL y se colapsa lo anterior. Por debajo
  // del umbral no se colapsa nada: una barra de «1 entrada anterior» para
  // ahorrar una línea es peor que la línea.
  const THREAD_TAIL = 4;
  const hiddenCount = threadExpanded || timeline.length <= THREAD_TAIL + 1
    ? 0
    : timeline.length - THREAD_TAIL;
  const visibleTimeline = hiddenCount > 0 ? timeline.slice(hiddenCount) : timeline;

  /** Adjuntos que llegaron CON un mensaje del hilo, indexados por mensaje.
   *
   *  Desde el paso 34 las capturas también: son adjuntos con tipo de imagen y
   *  heredan el `message_id` que los ficheros ya tenían (K6, K22). Así van
   *  dentro de la burbuja del mensaje que las trajo, y la galería aparte —que
   *  se comía el detalle— ya no existe. */
  const filesByMessage = new Map<string, TicketFile[]>();
  for (const f of selected?.files ?? []) {
    if (!f.messageId) continue;
    const list = filesByMessage.get(f.messageId) ?? [];
    list.push(f);
    filesByMessage.set(f.messageId, list);
  }
  /** Los que NO cuelgan de ningún mensaje: vinieron con el alta. Esos van
   *  bajo la descripción, que es donde se cuenta el problema. */
  const introFiles = (selected?.files ?? []).filter((f) => !f.messageId);
  /** Las capturas del ticket en orden de llegada: lo que recorre la caja de
   *  luz con sus flechas. */
  const imagenes = (selected?.files ?? []).filter(esImagen);
  const abrirImagen = (f: TicketFile) => setVisor(Math.max(0, imagenes.findIndex((i) => i.id === f.id)));
  /** De qué mensaje es cada adjunto, para la lista «Adjuntos (n)»: quién lo
   *  mandó y si es de una nota interna. Sale del hilo ya cargado. */
  const origenAdjuntos = new Map<string, OrigenAdjunto>();
  for (const evt of timeline) {
    if (evt.type !== 'message') continue;
    const d = evt.details;
    origenAdjuntos.set(String(d.id), {
      autor: evt.actorName ?? (d.author_type === 'user' ? selected?.reporterName ?? null : null),
      interno: d.visibility === 'internal',
    });
  }

  return (
    <div className={styles.page}>
      {personaOpen && (
        <PersonaModal
          initialEmail={createReporterEmail.trim()}
          // El nombre que ya se escribió; si está vacío, la parte local del
          // correo, que es mejor que dejarlo en blanco y se puede editar luego.
          initialName={createReporterName.trim() || createReporterEmail.trim().split('@')[0]}
          onClose={() => setPersonaOpen(false)}
          onSaved={(p, { accessSent }) => {
            setPersonaOpen(false);
            setMessage(accessSent
              ? `Cuenta creada para ${p.email}. Se le ha enviado el enlace de acceso al portal.`
              : `Cuenta creada para ${p.email}, sin enviarle el acceso.`);
            setEmailLookup({ exists: true, displayName: p.displayName, isStaff: false, isActive: p.isLive });
          }}
        />
      )}
      {/* La cinta es CONTEXTUAL: con un ticket abierto dice cuál, como el
          documento de diseño de Jose (2026-09-25). No es adorno — en «sin
          vista previa» el detalle ocupa la pantalla entera y la cinta es lo
          único que queda diciendo dónde estás.

          Lo que el documento pone aquí y NO se maqueta es «Exportar»: no
          existe tal cosa, y un botón que no hace nada es peor que su hueco
          (regla de K29). */}
      <Topbar
        title={selected ? `Ticket ${selected.reference}` : 'Bandeja de tickets'}
        subtitle={selected ? selected.title : 'Todo el soporte entrante, en una sola bandeja'}
        moduleIcon={<ModuleIcon weight="duotone" size={18} color={mc.accent} />}
        moduleColor={mc.accent}
        moduleBg={mc.bg}
        rightSlot={
          /* Por debajo de 900px el modo lo impone la ventana, así que el
             conmutador se esconde en vez de mentir: los tres botones estarían
             ahí y dos de ellos no harían nada. */
          narrow ? undefined : <ViewModeToggle value={paneMode} onChange={changePaneMode} />
        }
        action={canCreate ? { label: '+ Nuevo ticket', onClick: () => { setShowCreateForm(true); setSelected(null); } } : undefined}
      />
      {/* El buscador es CLIENT-SIDE: filtra las hasta 100 filas ya cargadas y
          no pregunta al servidor. El placeholder lo dice para que nadie
          concluya «no existe» de un resultado vacío. La búsqueda de verdad es
          entrega 2 y va con su propio repaso de seguridad: un buscador de
          servidor que distinga «no existe» de «no es tuyo» es un oráculo de
          enumeración. */}
      {/* La barra de filtros solo se monta si hay una LISTA que filtrar. En
          «sin vista previa» con algo abierto la lista no está en pantalla, y
          dejar ahí las pestañas y los desplegables es ofrecer controles que no
          mueven nada de lo que se ve — el mismo criterio por el que el
          conmutador de vistas desaparece en ventana estrecha. */}
      {listVisible && (
      <FilterBar
        layout="stacked"
        tabs={viewTabs}
        activeTab={viewTab}
        onTabChange={(v) => setViewTab(v as '' | 'unassigned' | 'mine')}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Filtrar los tickets cargados…"
        meta={`${filteredItems.length} ${filteredItems.length === 1 ? 'ticket' : 'tickets'}`
          + (unassignedCount > 0 ? ` \u00B7 ${unassignedCount} sin asignar` : '')}
        filters={(
          <>
            <select
              className={filterBar.select}
              data-on={statusFilter ? 'true' : 'false'}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Filtrar por estado"
            >
              <option value="">Estado</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]} ({items.filter((i) => i.status === s).length})
                </option>
              ))}
            </select>
            <select
              className={filterBar.select}
              data-on={priorityFilter ? 'true' : 'false'}
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              aria-label="Filtrar por prioridad"
            >
              <option value="">Prioridad</option>
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {PRIORITY_LABELS[p]} ({items.filter((i) => i.priority === p).length})
                </option>
              ))}
            </select>
            {canTouchAssignment && areaOptions.length > 0 && (
              <select
                className={filterBar.select}
                data-on={areaFilter ? 'true' : 'false'}
                value={areaFilter}
                onChange={(e) => setAreaFilter(e.target.value)}
                aria-label="Filtrar por equipo"
              >
                {/* La «Cola» del prototipo es el Equipo (`area`), y es un
                    equipo INTERNO: no un cliente. La entidad Cliente final no
                    existe todavía y el término se reserva para ella. */}
                <option value="">Equipo</option>
                {areaOptions.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            )}
            {canAssign && staffUsers.length > 0 && (
              <select
                className={filterBar.select}
                data-on={assigneeFilter ? 'true' : 'false'}
                value={assigneeFilter}
                onChange={(e) => setAssigneeFilter(e.target.value)}
                aria-label="Filtrar por técnico"
              >
                <option value="">Técnico</option>
                <option value="__none__">Sin técnico</option>
                {staffUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.displayName ?? u.email}</option>
                ))}
              </select>
            )}
            {clienteOptions.length > 0 && (
              <select
                className={`${filterBar.select} ${styles.clienteFilter}`}
                data-on={clienteFilter ? 'true' : 'false'}
                value={clienteFilter}
                onChange={(e) => setClienteFilter(e.target.value)}
                aria-label="Filtrar por cliente"
              >
                <option value="">Cliente</option>
                {sinClienteCount > 0 && <option value="__none__">Sin cliente ({sinClienteCount})</option>}
                {clienteOptions.map(([id, nombre]) => (
                  <option key={id} value={id}>
                    {nombre} ({items.filter((i) => i.clienteId === id).length})
                  </option>
                ))}
              </select>
            )}
          </>
        )}
      />
      )}

    {/* `hasSelection` significa «el panel de la derecha tiene algo que
        enseñar», y el formulario de alta TAMBIÉN cuenta.

        ⚠️ Hasta aquí era solo `selected`, y eso escondía el alta: la regla
        `.layout:not(.hasSelection) .detailPanel { display: none }` la puso el
        paso 36 para que el «Selecciona un ticket» no se comiera media
        pantalla, pero «+ Nuevo ticket» deja `selected` a null a propósito —
        así que pulsarlo no abría NADA por encima de 900px. Comprobado en el
        navegador antes de tocarlo: el panel salía con `display:none` y el
        formulario medía 0×0. */}
    <div
      className={`${styles.layout} ${splitOpen ? styles.hasSelection : ''}`}
      data-pane={effectivePane}
    >
      {/* ── Left: list ──────────────────────────────────── */}
      <div
        className={styles.listPanel}
        data-compact={listCompact ? 'true' : 'false'}
        /* El tamaño guardado solo manda cuando el panel comparte sitio con el
           detalle, y en el eje que toque: ancho si el detalle está al lado,
           alto si está debajo. En «sin vista previa» no se fija ninguno. */
        style={
          splitOpen && effectivePane === 'right' ? { width: listPaneWidth }
          : splitOpen && effectivePane === 'bottom' ? { height: listPaneHeight }
          : undefined
        }
      >
        {loading && <p className={styles.emptyText}>Cargando…</p>}

        {!loading && filteredItems.length === 0 && (
          <p className={styles.emptyText}>
            No hay tickets{viewTab || statusFilter || priorityFilter || areaFilter || assigneeFilter || clienteFilter || search
              ? ' que cumplan el filtro' : ''}
          </p>
        )}

        {/* La bandeja tiene DOS formas y son la misma lista:
            · sin ticket abierto → TABLA con columnas, a todo el ancho.
            · con ticket abierto → TARJETAS compactas, porque el panel se
              estrecha a ~320 px y una tabla ahí no es una tabla, es un
              amasijo de columnas de 40 px.
            Es lo que enseñan las dos capturas del prototipo (la bandeja y la
            columna «Mis tickets abiertos» del detalle), leídas como lo que
            son: un solo componente en dos anchos. Y deja el trabajo del
            W3/30 medio hecho — la forma estrecha ya existe. */}
        {/* Cabecera de la lista, del v3: qué estás mirando, cuántos son y en
            qué orden. Va en las DOS formas de la lista y no solo junto al
            detalle: el orden elegido se aplica también a la tabla, y un orden
            que cambia la tabla sin un control visible en ella es un estado
            escondido. */}
        {!loading && (
          <div className={styles.listHead}>
            <span className={styles.listHeadTitle}>{LIST_TITLES[viewTab]}</span>
            <span className={styles.listHeadCount}>{filteredItems.length}</span>
            <label className={styles.listSort}>
              <span className={styles.srOnly}>Ordenar por</span>
              <select
                className={styles.listSortSelect}
                value={sortMode}
                onChange={(e) => changeSortMode(e.target.value as SortMode)}
              >
                {(Object.keys(SORT_LABELS) as SortMode[]).map((m) => (
                  <option key={m} value={m}>{SORT_LABELS[m]}</option>
                ))}
              </select>
            </label>
          </div>
        )}

        {!loading && filteredItems.length > 0 && !listCompact && (
          <div className={styles.tableHead}>
            {/* Seis celdas para seis columnas. Hasta el paso 36 había aquí un
                `<span aria-hidden />` de relleno porque la fila tenía una
                columna extra para el tile del icono; al quitarlo, esta celda
                sobra — y dejarla corría TODAS las etiquetas una posición. */}
            <span className={styles.thRef}>Referencia</span>
            <span className={styles.thSubject}>Asunto</span>
            <span className={styles.thUser}>Usuario · cliente</span>
            <span className={styles.thStatus}>Estado</span>
            <span className={styles.thAgent}>Técnico</span>
            {/* «SLA» como el documento v7. La celda lleva debajo «abierto
                hace…», que sigue sin color: es un hecho, no un plazo. */}
            <span className={styles.thAge}>SLA</span>
          </div>
        )}

        <div className={styles.table}>
          {filteredItems.map((item) => {
            const v = categoryVisual(item.category);
            const SourceIcon = SOURCE_ICONS[item.source];
            const sourceLabel = SOURCE_LABELS[item.source] ?? item.source;
            const sla = slaVista(item, now);
            const row = (
              <button
                key={item.id}
                type="button"
                className={`${styles.row} ${selected?.id === item.id ? styles.rowSelected : ''}`}
                onClick={() => { handleSelect(item); setShowCreateForm(false); }}
              >
                {/* Punto de categoría + referencia en la MISMA celda, como el
                    documento de diseño.

                    Hasta el paso 36 esto era un tile de 30px con el icono de
                    la categoría, y se defendía diciendo que «un punto de color
                    sin leyenda no se puede leer». El argumento era flojo: la
                    categoría YA va escrita en la meta-línea de aquí abajo
                    («Correo · Hardware»), así que el punto nunca fue la única
                    vía — es refuerzo, no código. Y el tile costaba una columna
                    de 30px + 12 de hueco que le faltaban al ASUNTO, que es lo
                    único que se lee de verdad en una bandeja: con él, la
                    meta-línea se cortaba a media palabra contra la columna
                    Usuario. */}
                <span className={styles.cellRef}>
                  <span className={styles.catDot} style={{ background: v.accent }} aria-hidden />
                  {item.reference}
                </span>

                <span className={styles.rowBody}>
                  <span className={styles.rowTitle}>
                    <span className={styles.rowTitleText}>{item.title}</span>
                    {/* Solo alta y crítica. `media` es el default y saldría en
                        casi todas las filas: un chip repetido en todas partes
                        no informa y le quita fuerza al que sí. */}
                    {isNotablePriority(item.priority) && (
                      <Badge tone={priorityTone(item.priority)} variant="outline">
                        {priorityLabel(item.priority)}
                      </Badge>
                    )}
                  </span>
                  <span className={styles.rowMeta}>
                    {/* Canal con ICONO y categoría con palabra: «Correo» es las
                        dos cosas y en la misma línea se leería «Correo · Correo». */}
                    {SourceIcon && <SourceIcon size={12} weight="regular" aria-hidden />}
                    {/* ⚠️ El texto va DENTRO de un span propio. `.rowMeta` es un
                        flex, y `text-overflow: ellipsis` no se aplica a un
                        contenedor flex: con el texto suelto aquí, la línea se
                        cortaba a media palabra y sin puntos suspensivos contra
                        la columna Usuario — parecía un fallo de pintado. El
                        ellipsis necesita un bloque con overflow propio. */}
                    <span className={styles.rowMetaText}>
                      {sourceLabel}{' · '}{item.category}
                      {item.assignedAreaName && ` · ${item.assignedAreaName}`}
                      {/* Solo en la tarjeta (CSS): en la tabla el cliente va
                          bajo la persona, en su columna. */}
                      {item.clienteNombre && (
                        <span className={styles.metaCliente}>{` · ${item.clienteNombre}`}</span>
                      )}
                    </span>
                  </span>
                </span>

                {/* Persona arriba y su cliente debajo (85b). En la misma celda y no
                    en una séptima columna: a 1440 con el aside, otra columna de
                    140px dejaba el ASUNTO en ~170px, y es lo único que se lee de
                    verdad. La fila ya tiene dos líneas por el asunto, así que no
                    crece. Dos bloques con su propio overflow: `text-overflow` no
                    hace nada sobre un flex. */}
                <span
                  className={styles.cellUser}
                  title={[item.reporterName ?? 'Anónimo', item.clienteNombre].filter(Boolean).join(' · ')}
                >
                  <span className={styles.cellUserName}>{item.reporterName ?? 'Anónimo'}</span>
                  <span className={styles.cellUserCliente} data-empty={item.clienteNombre ? 'false' : 'true'}>
                    {item.clienteNombre ?? 'Sin cliente'}
                  </span>
                </span>

                <span className={styles.cellStatus}>
                  <Badge tone={STATUS_TONE[item.status] ?? 'slate'}>{STATUS_LABELS[item.status]}</Badge>
                </span>

                <span className={styles.cellAgent}>
                  <span
                    className={styles.avatar}
                    data-empty={item.assigneeName ? 'false' : 'true'}
                    aria-hidden
                  >
                    {item.assigneeName ? initials(item.assigneeName) : '—'}
                  </span>
                  <span className={styles.cellAgentName}>{item.assigneeName ?? 'Sin asignar'}</span>
                </span>

                {/* SLA arriba (paso 89) y «abierto hace» debajo, en la MISMA
                    celda: una séptima columna dejaba el ASUNTO sin sitio (el
                    mismo cálculo que en el 85b con el cliente). Sin
                    vencimiento —una BD sin pack— queda solo la antigüedad. */}
                <span className={styles.cellSla}>
                  {sla && (
                    <span className={styles.slaText} data-tono={sla.tono} title={sla.detalle}>
                      {sla.texto}
                    </span>
                  )}
                  <span className={styles.cellElapsed} title={`Creado el ${formatDate(item.createdAt)}`}>
                    {sla ? `abierto hace ${formatElapsed(item.createdAt, now)}` : formatElapsed(item.createdAt, now)}
                  </span>
                </span>
              </button>
            );
            return row;
          })}
        </div>
      </div>

      {/* ── Aside de la bandeja ─────────────────────────────────
          250px a la derecha de la lista, como el documento de diseño, y
          SOLO cuando no hay ticket abierto: con el detalle abierto el
          espacio es del hilo, y el propio documento lo sustituye allí por
          su aside de 292px.

          De los dos bloques que dibuja el documento aquí, este es el único
          que existe. «Vistas guardadas» necesita poder guardar una vista,
          que es C2/90: sin eso serían cinco filas de adorno, o peor, cinco
          filas guardadas en `localStorage` que se pierden al cambiar de
          navegador y que nadie ha decidido cómo se comparten. Ver K29. */}
      {!splitOpen && !loading && filteredItems.length > 0 && (
        <aside className={styles.inboxAside} aria-label="Carga del equipo">
          <div className={styles.asideBlock}>
            <div className={styles.asideTitle}>Carga del equipo</div>
            <div className={styles.workload}>
              {workload.map((w) => (
                <div key={w.name} className={styles.workRow}>
                  <div className={styles.workHead}>
                    <span className={styles.workName} title={w.name}>{w.name}</span>
                    <span className={styles.workCount}>{w.count}</span>
                  </div>
                  <div className={styles.workTrack}>
                    <div
                      className={styles.workBar}
                      data-unassigned={w.unassigned ? 'true' : 'false'}
                      style={{ width: `${w.pct}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            {/* El rótulo dice de dónde sale el número. Sin esta línea, cuatro
                barras encima de «26 tickets» se leen como la carga total del
                equipo, y solo son los tickets de esta página. */}
            <p className={styles.asideNote}>
              Tickets abiertos de los que hay cargados en la lista.
            </p>
          </div>
        </aside>
      )}

      {/* Solo hay nada que redimensionar cuando el detalle está abierto, y el
          tirador que toca es el del eje en que se reparten: vertical entre dos
          columnas, horizontal entre dos filas. En «sin vista previa» no hay
          reparto y no hay tirador. */}
      {splitOpen && effectivePane === 'right' && <PaneResizer {...resizerProps} />}
      {splitOpen && effectivePane === 'bottom' && <PaneResizer {...resizerVProps} />}

      {/* ── Right: detail ───────────────────────────────── */}
      <div className={styles.detailPanel}>
        {showCreateForm ? (
          <div className={styles.createForm}>
            <button className={styles.backBtn} onClick={() => setShowCreateForm(false)}>
              <ArrowLeft weight="regular" size={14} /> Cancelar
            </button>
            <h2 className={styles.detailTitle}>Nuevo ticket</h2>

            <div className={styles.actionSection}>
              <h3 className={styles.actionTitle}>Usuario que reporta</h3>
              <input
                className={styles.input}
                type="text"
                value={createReporterName}
                onChange={(e) => setCreateReporterName(e.target.value)}
                placeholder="Nombre del usuario"
              />
              {/* Teléfono y email comparten fila: son las DOS vías de contacto
                  de la misma persona y se rellenan a la vez mientras hablas con
                  ella. Apilados gastaban dos líneas para dos campos cortos. */}
              <div className={styles.formRow}>
                <input
                  className={styles.input}
                  type="text"
                  value={createReporterPhone}
                  onChange={(e) => setCreateReporterPhone(e.target.value)}
                  placeholder="Teléfono de contacto"
                />
                <input
                  className={styles.input}
                  type="email"
                  value={createReporterEmail}
                  onChange={(e) => setCreateReporterEmail(e.target.value)}
                  placeholder="Email del usuario *"
                  aria-invalid={emailMalFormado ? 'true' : undefined}
                />
              </div>
              {/* ⚠️ Cuatro estados, y ninguno BLOQUEA el alta. El backend acepta
                  cualquier cadena en `reporter_email` y seguirá aceptándola:
                  poder abrir un ticket para alguien que todavía no es usuario
                  es parte del diseño —el enlace del portal lo adopta luego—.
                  Lo que no puede pasar es que nadie se entere de que ese
                  ticket va a quedar invisible para el cliente. */}
              {emailMalFormado && (
                <p className={styles.formHint}>
                  Eso no parece un correo. Si lo dejas así, el usuario no recibirá el aviso
                  ni verá el ticket en el portal.
                </p>
              )}
              {emailChecking && !emailMalFormado && (
                <p className={styles.formHintMuted}>Comprobando la dirección…</p>
              )}
              {emailLookup?.exists && !emailLookup.isStaff && (
                <p className={styles.formHintOk}>
                  ✓ {emailLookup.displayName ?? 'Cuenta'} ya tiene acceso al portal
                  {emailLookup.isActive ? '' : ' (cuenta desactivada)'}.
                  {/* El cliente sale de la persona, sin selector propio (85b):
                      se dice aquí para que quien abre el ticket lo vea ANTES. */}
                  {' '}{emailLookup.clienteNombre
                    ? <>El ticket será de <strong>{emailLookup.clienteNombre}</strong>.</>
                    : 'No tiene cliente: el ticket nacerá sin él.'}
                </p>
              )}
              {emailLookup?.exists && emailLookup.isStaff && (
                <p className={styles.formHint}>
                  Esa dirección es de una cuenta interna, no de un cliente. No se le emite
                  enlace del portal, así que no verá el ticket ahí.
                </p>
              )}
              {emailLookup && !emailLookup.exists && (
                <div className={styles.formHintRow}>
                  {/* ⚠️ Este texto decía «el ticket se creará igual, pero el
                      usuario no lo verá en el portal». Dejó de ser verdad con la
                      regla de «todo ticket tiene dueño»: ahora NO se crea. Se
                      pilló al probar la pantalla con la cuenta de técnico —el
                      aviso contradecía al botón bloqueado de justo debajo—, no
                      leyendo el código. */}
                  <p className={styles.formHint}>
                    No hay ninguna cuenta con ese correo, y todo ticket necesita dueño.
                    Créala para poder abrirlo.
                  </p>
                  {canCreateRequester ? (
                    <button
                      type="button"
                      className={styles.hintBtn}
                      onClick={handleCreateAccount}
                    >
                      Crear cuenta…
                    </button>
                  ) : (
                    <span className={styles.formHintMuted}>
                      Pídele a un administrador que la cree.
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className={styles.actionSection}>
              <h3 className={styles.actionTitle}>Ticket</h3>
              <input
                className={styles.input}
                type="text"
                value={createTitle}
                onChange={(e) => setCreateTitle(e.target.value)}
                placeholder="Título *"
              />
              <textarea
                className={styles.textarea}
                value={createDesc}
                onChange={(e) => setCreateDesc(e.target.value)}
                onPaste={(e) => {
                  const pegadas = capturasPegadas(e);
                  if (pegadas.length === 0) return;
                  e.preventDefault();
                  const r = sumarAdjuntos(createFiles, pegadas, MAX_ADJUNTOS_PANEL);
                  setCreateFiles(r.archivos);
                  setCreateFilesAviso(r.aviso);
                }}
                placeholder="Descripción *"
                rows={3}
              />
              {/* Lo que el usuario mandó por otro lado (la captura del
                  WhatsApp, el log de la llamada). Va con la descripción, y el
                  cliente lo ve en su portal igual que ella. */}
              <div className={styles.createAttach}>
                <BotonAdjuntar
                  conTexto
                  disabled={creating}
                  onElegir={(nuevos) => {
                    const r = sumarAdjuntos(createFiles, nuevos, MAX_ADJUNTOS_PANEL);
                    setCreateFiles(r.archivos);
                    setCreateFilesAviso(r.aviso);
                  }}
                />
                <AdjuntosPendientes
                  archivos={createFiles}
                  aviso={createFilesAviso}
                  onQuitar={(i) => {
                    setCreateFiles((cur) => cur.filter((_, j) => j !== i));
                    setCreateFilesAviso(null);
                  }}
                />
              </div>
              {/* Categoría, prioridad y canal en UNA fila: los tres son
                  clasificación del mismo ticket y se eligen de un vistazo.
                  El canal era un bloque propio con tres botones al principio
                  del formulario — una sección entera, con su rótulo, para un
                  dato que no es lo primero que preguntas por teléfono. */}
              <div className={styles.formRow}>
                <select
                  className={styles.select}
                  value={createCategory}
                  onChange={(e) => setCreateCategory(e.target.value)}
                >
                  <option value="">Categoría *</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.name}>{c.icon} {c.name}</option>
                  ))}
                </select>
                <select
                  className={styles.select}
                  value={createPriority}
                  onChange={(e) => setCreatePriority(e.target.value as Priority)}
                  title={PRIORITY_HINTS[createPriority]}
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p}>Prioridad: {PRIORITY_LABELS[p]}</option>
                  ))}
                </select>
                <select
                  className={styles.select}
                  value={createSource}
                  onChange={(e) => setCreateSource(e.target.value as 'phone' | 'email' | 'in_person')}
                >
                  {MANUAL_SOURCES.map((s) => (
                    <option key={s} value={s}>Canal: {SOURCE_LABELS[s]}</option>
                  ))}
                </select>
              </div>
              <input
                className={styles.input}
                type="text"
                value={createAddress}
                onChange={(e) => setCreateAddress(e.target.value)}
                placeholder="Dirección (opcional)"
              />
            </div>

            {canAssign && (
              <div className={styles.actionSection}>
                <h3 className={styles.actionTitle}>Asignación</h3>
                <div className={styles.actionRow}>
                  <select
                    className={styles.select}
                    value={createAreaId}
                    onChange={(e) => { setCreateAreaId(e.target.value); setCreateAssignTo(''); }}
                  >
                    <option value="">Sin equipo</option>
                    {areas.map((a) => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                  <select
                    className={styles.select}
                    value={createAssignTo}
                    onChange={(e) => setCreateAssignTo(e.target.value)}
                  >
                    <option value="">Sin técnico</option>
                    {createFilteredStaff.map((u) => (
                      <option key={u.id} value={u.id}>{u.displayName ?? u.email}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <button
              type="button"
              className={styles.actionButton}
              onClick={handleCreate}
              /* ⚠️ Sin cuenta NO se crea el ticket (decisión de Jose,
                 2026-09-26): todo ticket tiene dueño. El backend devuelve 409
                 `REPORTER_NOT_FOUND` si esa dirección no existe, así que esto
                 no es la barrera —es no ofrecer un botón que va a fallar—. La
                 salida está justo encima: «Crear cuenta…» (el formulario único de persona, 85a). */
              disabled={
                creating || !createTitle.trim() || !createDesc.trim() || !createCategory
                || !emailLookup?.exists || emailChecking
              }
              style={{ width: '100%', marginTop: 8 }}
            >
              {creating ? 'Creando…' : 'Crear ticket'}
            </button>

            {/* Un botón gris sin explicación se lee como «esto está roto». */}
            {/* Solo cuando NO hay nada escrito: si hay un correo sin cuenta, el
                aviso de arriba ya lo explica y repetirlo es ruido. */}
            {!createReporterEmail.trim() && createTitle.trim() && (
              <p className={styles.formHintMuted}>
                Indica el correo del usuario: todo ticket tiene que tener dueño.
              </p>
            )}

            {message && <p className={styles.message}>{message}</p>}
          </div>
        ) : !selected ? (
          <p className={styles.emptyText}>Selecciona un ticket</p>
        ) : (
          <>
            <div className={styles.detailHead}>
              <button className={styles.backBtn} onClick={() => setSelected(null)}>
                <ArrowLeft weight="regular" size={14} /> Volver a la lista
              </button>
              {frozen && (
                <p className={styles.frozenBanner} role="status">
                  <strong>Cliente desactivado.</strong> Este ticket ya no se puede editar: solo
                  cerrarlo desde <strong>Gestión</strong>, y al cliente no le llega ningún aviso.
                </p>
              )}
              {/* ⚠️ TRES líneas, como el v3 (paso 43): situación arriba, título
                  en medio, identidad debajo. El paso 40 las había fundido en
                  una fila —título a la izquierda, racimo a la derecha— para
                  ahorrar alto; el documento de Jose las apila y eso manda. Se
                  pagan ~36px de cabecera a cambio de que el título tenga la
                  anchura entera y no se parta contra los chips.
                  Lo que sí se conserva del 40: el racimo es UN `<div>`, así que
                  en estrecho baja entero, y «Resolver» vive en esa fila.
                  Del prototipo se caen «Primera respuesta en 12 min» (es SLA)
                  y «Fusionar» (entrega 2). */}
              <div className={styles.detailStatusBar}>
                {/* En el detalle SÍ se pinta siempre la prioridad, también
                    `media`: aquí no compite con nada y su ausencia se leería
                    como "este ticket no tiene prioridad". El chip va con
                    contorno y el estado relleno, así que aunque coincidan de
                    tono no se confunden. */}
                <Badge tone={STATUS_TONE[selected.status] ?? 'slate'}>{STATUS_LABELS[selected.status]}</Badge>
                <Badge
                  tone={priorityTone(selected.priority)}
                  variant="outline"
                  title={PRIORITY_HINTS[selected.priority as Priority]}
                >
                  {priorityLabel(selected.priority)}
                </Badge>
                <span className={styles.detailAge} title={`Creado el ${formatDate(selected.createdAt)}`}>
                  Abierto hace {formatElapsed(selected.createdAt, now)}
                </span>
                {/* El SLA de primera respuesta (paso 89), con el mismo texto y
                    tono que la columna de la bandeja. */}
                {selectedSla && (
                  <span className={styles.slaText} data-tono={selectedSla.tono} title={selectedSla.detalle}>
                    1.ª respuesta: {selectedSla.texto}
                  </span>
                )}
                {/* Atajo a la acción que se hace mil veces al día. NO resuelve
                    por su cuenta: deja «Gestión» abierta y con el estado ya
                    puesto en `resuelta`, que es donde vive el aviso de «no has
                    marcado qué respuesta resolvió esto» y el botón que remata.
                    Resolver de un clic se saltaría ese aviso, y ese aviso es lo
                    único que alimenta el corpus del borrador con IA.
                    El v3 no lo dibuja; se queda porque se usa (paso 43, B6). */}
                {/* ⚠️ Se mira el GRAFO del paso 81, no `status !== 'resuelta'`:
                    `resuelta` solo se alcanza desde `en_progreso`. Sin esta
                    guarda, el atajo ofrecía desde un ticket `nueva` una
                    transición que el backend rechaza con 400 — un botón
                    primario que no hace nada, que es peor que no tenerlo. */}
                {canUpdateStatus && (STATUS_TRANSITIONS[selected.status] ?? []).includes('resuelta') && (
                  <button
                    type="button"
                    className={styles.resolveBtn}
                    onClick={() => { setNewStatus('resuelta'); setOpenGestion((n) => n + 1); }}
                  >
                    Resolver
                  </button>
                )}
              </div>
              <h2 className={styles.detailTitleBig}>{selected.title}</h2>
              {/* Línea de identidad del ticket, como en el prototipo:
                  referencia · canal · categoría · usuario. La referencia es
                  `KV-2608-4H7Q`, NUNCA el correlativo: dos correlativos
                  restados revelan el volumen de la empresa de soporte (paso
                  84b). */}
              <div className={styles.detailIdLine}>
                <span className={styles.detailRef}>{selected.reference}</span>
                <span className={styles.detailIdSep}>·</span>
                {(() => {
                  const SrcIcon = SOURCE_ICONS[selected.source];
                  return SrcIcon ? <SrcIcon size={13} weight="regular" aria-hidden /> : null;
                })()}
                <span>{SOURCE_LABELS[selected.source] ?? selected.source}</span>
                <span className={styles.detailIdSep}>·</span>
                <span>{selected.category}</span>
                <span className={styles.detailIdSep}>·</span>
                <span>{selected.reporterName ?? 'Anónimo'}</span>
                {/* El cliente, también aquí (85b): por debajo de 1280px el aside
                    de propiedades se va y esta línea es lo que queda. */}
                {selected.clienteNombre && (
                  <>
                    <span className={styles.detailIdSep}>·</span>
                    <span>{selected.clienteNombre}</span>
                  </>
                )}
              </div>
            </div>

            {/* ── Detalle en dos columnas ──────────────────────────
                Hilo en el centro, aside de 292px a la derecha, como el
                documento de diseño. Antes las propiedades iban apiladas
                bajo el título, y eso empujaba la conversación —que es lo
                que se viene a leer— por debajo del pliegue. */}
            <div className={styles.detailBody}>
            <aside className={styles.detailAside} aria-label="Propiedades del ticket">
              {/* ⚠️ Esta es la ÚNICA tarjeta del aside del documento que
                  tiene datos detrás. Las otras cuatro que dibuja —ficha y
                  histórico del cliente, SLA de resolución, activos del
                  equipo— no se pintan, ni siquiera como maqueta: enseñarle
                  a un comprador un SLA que no existe es prometer una
                  funcionalidad. Cliente es C1, SLA es C2/89 y los activos
                  están fuera del mercado por decisión. Ver K29. */}
              <div className={styles.asideCard}>
                <div className={styles.asideTitle}>Propiedades</div>
                <div className={styles.metaCells}>
                {([
                  { label: 'Categoría', value: selected.category },
                  // El CANAL se guardaba desde siempre y no se veía en ninguna
                  // pantalla: saber si esto entró por teléfono o por correo
                  // cambia cómo se responde. Va con icono y no solo con
                  // palabra porque «Correo» es también una categoría del seed
                  // y las dos celdas son vecinas — ver SOURCE_LABELS.
                  {
                    label: 'Canal',
                    value: SOURCE_LABELS[selected.source] ?? selected.source,
                    icon: SOURCE_ICONS[selected.source],
                  },
                  { label: 'Equipo asignado', value: selected.assignedAreaName ?? '—', muted: !selected.assignedAreaName },
                  { label: 'Técnico', value: selected.assigneeName ?? 'Sin asignar', muted: !selected.assigneeName },
                  { label: 'Cliente', value: selected.clienteNombre ?? 'Sin cliente', muted: !selected.clienteNombre },
                  { label: 'Usuario', value: selected.reporterName ?? 'Anónimo' },
                  { label: 'Recibido', value: formatDate(selected.createdAt) },
                  // SLA (paso 89): el vencimiento y de qué pack sale. Solo si
                  // el ticket lo tiene (una BD sin pack estándar no lo pone).
                  ...(selected.firstResponseDueAt
                    ? [
                        {
                          // Corto, y el verbo en el rótulo: la celda es
                          // estrecha y «respondida 06/10 19:56» ya se cortaba.
                          label: selected.firstResponseAt ? 'Respondido' : 'Responder antes de',
                          value: fechaCorta(selected.firstResponseAt ?? selected.firstResponseDueAt),
                        },
                        {
                          label: 'SLA',
                          value: [selected.slaPackNombre, selected.slaPlazoMin && `${slaDuracion(selected.slaPlazoMin)} laborables`]
                            .filter(Boolean).join(' · ') || '—',
                        },
                      ]
                    : []),
                  { label: 'Dirección', value: selected.address ?? '—', muted: !selected.address },
                ] as { label: string; value: string; muted?: boolean; icon?: Icon }[]).map((c) => (
                  <div key={c.label} className={styles.metaCell}>
                    <span className={styles.metaCellLabel}>{c.label}</span>
                    <span className={styles.metaCellValue} data-muted={c.muted ? 'true' : 'false'} title={c.value}>
                      {c.icon && <c.icon weight="regular" size={13} aria-hidden />}
                      {/* El texto en su propio bloque: `.metaCellValue` es un
                          flex, y ahí el ellipsis no hace nada. Un cliente de
                          nombre largo se cortaba contra el borde sin puntos
                          (85b); a «Dirección» le pasaba igual. */}
                      <span className={styles.metaCellText}>{c.value}</span>
                    </span>
                  </div>
                ))}
                </div>
              </div>
              {(selected.files ?? []).length > 0 && (
                <div className={styles.asideCard}>
                  <div className={styles.asideTitle}>Adjuntos ({selected.files.length})</div>
                  <ListaAdjuntos
                    files={selected.files}
                    origen={origenAdjuntos}
                    usuario={selected.reporterName}
                    onAbrirImagen={abrirImagen}
                    onDescargar={handleDownloadFile}
                    onBorrar={frozen ? undefined : handleDeleteFile}
                    ocupado={fileBusy}
                  />
                </div>
              )}
            </aside>

            <div className={styles.detailScroll}>
            <div className={styles.descBlock}>
              <div className={styles.sectionLabel}>Descripción</div>
              <div className={styles.descText}>{selected.description}</div>
              {/* Adjuntos DEL ALTA: con la descripción, que es donde se cuenta
                  el problema. Los de una respuesta van en su burbuja. */}
              <AdjuntosMensaje
                incidenciaId={selected.id}
                token={token}
                files={introFiles}
                onAbrirImagen={abrirImagen}
                onDescargar={handleDownloadFile}
              />
            </div>

            {/* «Adjuntos (n)» de la opción B (paso 34). Aquí solo por debajo
                de 1280 px, donde el aside se esconde: arriba es la tarjeta
                del aside. Un solo componente para los dos sitios. */}
            {(selected.files ?? []).length > 0 && (
              <div className={`${styles.asideCard} ${styles.adjuntosEnLinea}`}>
                <div className={styles.asideTitle}>Adjuntos ({selected.files.length})</div>
                <ListaAdjuntos
                  files={selected.files}
                  origen={origenAdjuntos}
                  usuario={selected.reporterName}
                  onAbrirImagen={abrirImagen}
                  onDescargar={handleDownloadFile}
                  onBorrar={frozen ? undefined : handleDeleteFile}
                  ocupado={fileBusy}
                />
              </div>
            )}

            {(canAssign || canUpdateStatus) && (
              <DetailSection
                title="Gestión"
                storageKey="incidencias.gestion"
                openSignal={openGestion}
                right={(
                  <span className={styles.assignChip} data-assigned={selected.assigneeName ? 'true' : 'false'}>
                    {/* «Asignado · X», con el prefijo del v3: sin él, un nombre
                        suelto en la barra no dice qué relación tiene con el
                        ticket. El color sigue siendo el semántico (C4). */}
                    {selected.assigneeName ? `Asignado \u00B7 ${selected.assigneeName}` : 'Sin asignar'}
                  </span>
                )}
              >
                <div className={styles.gestionSelects}>
                  {canTouchAssignment && !frozen && (
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>Equipo</span>
                      <select
                        className={styles.select}
                        value={assignAreaId}
                        onChange={(e) => { setAssignAreaId(e.target.value); setAssignToId(''); }}
                      >
                        <option value="">Sin equipo</option>
                        {areas.map((a) => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {canTouchAssignment && !frozen && (
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>Técnico</span>
                      <select
                        className={styles.select}
                        value={assignToId}
                        onChange={(e) => setAssignToId(e.target.value)}
                        disabled={assignLocked}
                        title={assignLocked
                          ? 'Este ticket ya lo lleva otra persona. Solo un coordinador puede reasignarlo.'
                          : undefined}
                      >
                        <option value="">Sin técnico</option>
                        {filteredStaff.map((u) => (
                          <option key={u.id} value={u.id}>{u.displayName ?? u.email} ({u.roleName})</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {canUpdateStatus && (
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>Estado</span>
                      <select
                        className={styles.select}
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                      >
                        {statusOptionsFrom(selected.status)
                          .filter((s) => !frozen || s === selected.status || CLOSED_STATUSES.includes(s))
                          .map((s) => (
                          <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {canUpdateStatus && (
                    <div className={styles.field}>
                      <span className={styles.fieldLabel}>Prioridad</span>
                      <select
                        className={styles.select}
                        value={selected.priority}
                        onChange={(e) => handlePriorityChange(e.target.value)}
                        disabled={saving || frozen}
                        title={PRIORITY_HINTS[selected.priority as Priority]}
                      >
                        {PRIORITY_OPTIONS.map((p) => (
                          <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                {/* Al resolver es cuando SÍ se sabe cuál fue la respuesta
                    buena: el usuario acaba de confirmar que funciona. Es el
                    único momento en que preguntarlo no es adivinar, así que el
                    aviso sale aquí y no antes. */}
                {canUpdateStatus && newStatus === 'resuelta' && selected.status !== 'resuelta'
                  && !timeline.some((e) => e.type === 'message' && e.details.is_resolutory) && (
                  <p className={styles.resolutoryHint}>
                    Ninguna respuesta está marcada como la que resolvió este ticket.
                    Márcala en <strong>Actividad</strong> antes de cerrarlo — de esas aprenderá
                    el asistente a redactar borradores.
                  </p>
                )}
                {/* D3 del paso 93 (Jose): suave, sin bloquear. Hay tickets que
                    se cierran sin trabajo (un duplicado) y uno que bloquee
                    acabaría con apuntes de 1 minuto para poder cerrar. Solo
                    sale cuando ya se sabe que hay 0 (`worklogs` cargado). */}
                {canUpdateStatus && canLogTime && newStatus === 'resuelta' && selected.status !== 'resuelta'
                  && worklogs?.totalMinutes === 0 && (
                  <p className={`${styles.resolutoryHint} ${styles.timeHint}`}>
                    Este ticket no tiene tiempo apuntado. Apúntalo en <strong>Tiempo</strong> antes
                    de cerrarlo: es lo que se factura.
                  </p>
                )}
                {canUpdateStatus && (
                  <div className={`${styles.field} ${styles.gestionNote}`}>
                    <span className={styles.fieldLabel}>Nota del cambio (opcional)</span>
                    <textarea
                      className={`${styles.textarea} ${styles.noteArea}`}
                      rows={3}
                      value={statusNote}
                      onChange={(e) => setStatusNote(e.target.value)}
                      placeholder="Se registra en el historial"
                    />
                  </div>
                )}
                <div className={styles.gestionActions}>
                  {canTouchAssignment && !frozen && (
                    <button type="button" className={`${styles.actionButton} ${styles.actionButtonSecondary}`} onClick={handleAssign} disabled={saving}>
                      {/* «Asignar» cuando repartes trabajo; «Cogerlo» cuando lo
                          único que puedes hacer es quedártelo tú. La etiqueta
                          describe la acción real, no el endpoint. */}
                      {canAssign ? 'Asignar' : 'Cogerlo'}
                      {/* Secundario con contorno, como el «Reasignar» del v3:
                          en esta tarjeta la acción que remata es «Actualizar». */}
                    </button>
                  )}
                  {canUpdateStatus && (
                    <button
                      type="button"
                      className={styles.actionButton}
                      onClick={handleStatusChange}
                      disabled={saving || newStatus === selected.status}
                    >
                      Actualizar
                    </button>
                  )}
                </div>
              </DetailSection>
            )}

            {/* Tiempo dedicado (paso 93). Debajo de Gestión y en su propia
                tarjeta: ver `WorklogSection`. */}
            <WorklogSection
              incidenciaId={selected.id}
              data={worklogs}
              token={token}
              canLog={canLogTime}
              frozen={frozen}
              onChanged={() => loadWorklogs(selected.id)}
            />

            {/* Actividad: UN SOLO relato. Mensajes al usuario, notas del
                equipo y eventos del ticket, intercalados por fecha. Antes
                estaban en tres secciones distintas y había que reconstruir
                mentalmente qué pasó y en qué orden. */}
            <DetailSection
              title="Actividad"
              storageKey="incidencias.actividad"
              right={<span className={styles.sectionCount}>{timeline.length} {timeline.length === 1 ? 'entrada' : 'entradas'}</span>}
            >
              {/* El prototipo colapsa con «2 mensajes anteriores · Ver todo».
                  Aquí se cuentan ENTRADAS y no mensajes, porque el relato de
                  Kivai lleva además los cambios de estado y las asignaciones
                  intercalados — el prototipo solo enseñaba mensajes y perdía
                  la mitad de la historia. */}
              {hiddenCount > 0 && (
                <button
                  type="button"
                  className={styles.tlMore}
                  onClick={() => setThreadExpanded(true)}
                >
                  <span>{hiddenCount} {hiddenCount === 1 ? 'entrada anterior' : 'entradas anteriores'}</span>
                  <span className={styles.tlMoreAction}>Ver todo</span>
                </button>
              )}

              <div className={styles.tl}>
                {visibleTimeline.map((evt, i) => {
                  const d = evt.details;

                  // Los mensajes se pintan como burbuja, no como línea de
                  // evento: son lo que se lee, el resto es contexto.
                  if (evt.type === 'message') {
                    const internal = d.visibility === 'internal';
                    const author = evt.actorName ?? (d.author_type === 'user' ? 'Usuario' : 'Sistema');
                    const msgFiles = filesByMessage.get(String(d.id)) ?? [];
                    const kind = internal ? 'internal' : d.direction === 'inbound' ? 'in' : 'out';
                    /* La ETIQUETA dice qué es la entrada, y sustituye a las dos
                       píldoras de antes («Nota interna» / «Visible para el
                       usuario») más el avatar y el nombre sueltos. Es del v3 y
                       es mejor señal: el dato que importa al leer un hilo no es
                       quién escribió, es **quién lo va a ver**, y eso ahora va
                       en el titular en vez de en un chip al lado. */
                    const label = internal
                      ? `Nota interna · ${author}`
                      : d.direction === 'inbound'
                        ? `Respuesta de ${author}`
                        : `Respuesta al usuario · ${author}`;
                    return (
                      <div key={i} className={styles.tlRow}>
                        <span className={styles.tlRail} aria-hidden>
                          <span className={styles.tlDot} data-kind={kind} />
                        </span>
                        <div className={styles.tlMain}>
                        <div className={styles.msgHead}>
                          {internal && <Lock weight="fill" size={10} className={styles.msgLock} />}
                          <span className={styles.msgAuthor}>{label}</span>
                          {d.channel === 'email' && (
                            <span className={styles.msgChannel}>por correo</span>
                          )}
                          {/* La hora va PEGADA al titular, como el v3, y no al
                              borde derecho: leída junto a quién habla es parte
                              de la frase; separada, era una columna que nadie
                              miraba. El borde derecho queda para la marca de
                              «Resuelve», que es una acción. */}
                          <span className={styles.msgTime}>{formatDate(evt.createdAt)}</span>
                          {/* Marcar cuál resolvió se hace AQUÍ, sobre el mensaje
                              ya enviado, y no con la casilla del formulario: al
                              escribir no sabes todavía si va a resolver nada.
                              De estas respuestas aprenderá el borrador con IA,
                              así que si nadie las marca el histórico llega sin
                              saber cuál era la buena. */}
                          {canReply && !frozen && !internal && d.direction === 'outbound' ? (
                            <button
                              type="button"
                              className={Boolean(d.is_resolutory) ? styles.msgResolutoryOn : styles.msgResolutoryOff}
                              onClick={() => handleToggleResolutory(String(d.id), !d.is_resolutory)}
                              disabled={resolutoryBusy}
                              title={Boolean(d.is_resolutory)
                                ? 'Esta es la respuesta que resolvió el ticket. Pulsa para desmarcarla.'
                                : 'Marcar como la respuesta que resolvió el ticket'}
                            >
                              {Boolean(d.is_resolutory) ? 'Resuelve' : 'Marcar como la que resuelve'}
                            </button>
                          ) : (
                            Boolean(d.is_resolutory) && (
                              <span className={styles.msgResolutory}>Resuelve</span>
                            )
                          )}
                        </div>
                        {/* La burbuja lleva SOLO el cuerpo y sus adjuntos. La
                            cabecera va fuera, sobre el fondo del panel, que es
                            lo que separa una entrada de la siguiente cuando hay
                            seis seguidas. */}
                        <div className={styles.bubble} data-kind={kind}>
                        <p className={styles.msgBody}>{String(d.body ?? '')}</p>
                        {/* «Adjunto la captura del error» con nada debajo era
                            el agujero del prototipo. Capturas y ficheros van
                            DENTRO de la burbuja del mensaje que los trajo
                            (pasos 33 y 34), que es como se leen. */}
                        <AdjuntosMensaje
                          incidenciaId={selected.id}
                          token={token}
                          files={msgFiles}
                          onAbrirImagen={abrirImagen}
                          onDescargar={handleDownloadFile}
                        />
                        </div>
                        </div>
                      </div>
                    );
                  }

                  let dot = 'var(--kv-n-600)';
                  let label = '';
                  if (evt.type === 'created') {
                    dot = STATUS_COLORS[d.status as string] ?? dot;
                    label = `Creada — ${STATUS_LABELS[d.status as string] ?? d.status}`;
                  } else if (evt.type === 'status_change') {
                    dot = STATUS_COLORS[d.new_status as string] ?? dot;
                    label = `${STATUS_LABELS[d.old_status as string] ?? d.old_status} → ${STATUS_LABELS[d.new_status as string] ?? d.new_status}`;
                  } else if (evt.type === 'assignment') {
                    dot = 'var(--st-revision-fg)';
                    const parts: string[] = [];
                    if (d.area_name) parts.push(`Área: ${d.area_name}`);
                    if (d.assignee_name) parts.push(`Técnico: ${d.assignee_name}`);
                    label = parts.length ? `Asignada — ${parts.join(', ')}` : 'Reasignada';
                  } else if (evt.type === 'note_update') {
                    // Rastro de cuando las notas vivían en admin_note: no tiene
                    // contenido que enseñar, solo la marca de que hubo cambio.
                    label = 'Nota interna actualizada (histórico)';
                  }
                  const note = evt.type === 'status_change' && typeof d.note === 'string' ? d.note : '';
                  /* Mensajes y eventos comparten AHORA la misma fila de
                     carril: un punto, una cabecera y, solo los mensajes, una
                     burbuja debajo. Antes eran dos lenguajes visuales
                     distintos en la misma lista —burbuja con avatar frente a
                     línea de timeline— y por eso el hilo se leía como dos
                     cosas pegadas en vez de como un relato. */
                  return (
                    <div key={i} className={styles.tlRow}>
                      <span className={styles.tlRail} aria-hidden>
                        {/* El punto de un EVENTO conserva el color de su
                            estado, que el v3 aplana a gris. Es información que
                            ya estaba y que se leía: quitarla para parecerse más
                            al documento sería cambiar señal por estética. */}
                        <span className={styles.tlDot} style={{ background: dot }} />
                      </span>
                      <div className={styles.tlMain}>
                        <div className={styles.msgHead}>
                          <span className={styles.msgAuthor}>{label}{evt.actorName ? ` · ${evt.actorName}` : ''}</span>
                          <span className={styles.msgTime}>{formatDate(evt.createdAt)}</span>
                        </div>
                        {note && <div className={styles.tlNote}>{note}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Paso 51b: lo que haría el asistente con ESTE ticket. Encima
                  del compositor, que es donde se decide qué hacer. */}
              {canVerTodo && !frozen && (
                <SiguientePaso
                  key={selected.id}
                  token={token}
                  incidenciaId={selected.id}
                  onCambio={() => {
                    const id = selected.id;
                    loadTimeline(id);
                    getIncidencia(id, token)
                      .then((fresh) => {
                        setSelected((cur) => (cur?.id === id ? fresh : cur));
                        setItems((cur) => cur.map((i) => (i.id === id ? fresh : i)));
                      })
                      .catch(() => {});
                  }}
                />
              )}
              {canReply && !frozen && (
                <div className={styles.composer}>
                  {/* El conmutador va ARRIBA y tiñe el composer entero: hay que
                      ver de un vistazo si lo que escribes lo va a leer el
                      usuario, antes de escribirlo. */}
                  <div className={styles.composerTabs}>
                    <button
                      type="button"
                      className={`${styles.composerTab} ${draftVisibility === 'internal' ? styles.composerTabOnInternal : ''}`}
                      onClick={() => { setDraftVisibility('internal'); setDraftResolutory(false); }}
                    >
                      <Lock weight="fill" size={11} /> Nota interna
                    </button>
                    <button
                      type="button"
                      className={`${styles.composerTab} ${draftVisibility === 'public' ? styles.composerTabOnPublic : ''}`}
                      onClick={() => setDraftVisibility('public')}
                    >
                      <ArrowBendUpLeft weight="bold" size={12} /> Respuesta al usuario
                    </button>
                  </div>
                  <textarea
                    className={`${styles.textarea} ${draftVisibility === 'internal' ? styles.textareaInternal : ''}`}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    // Una captura pegada (Impr Pant + Ctrl+V) se adjunta: es
                    // como llega casi siempre un pantallazo.
                    onPaste={(e) => {
                      const pegadas = capturasPegadas(e);
                      if (pegadas.length === 0) return;
                      e.preventDefault();
                      const r = sumarAdjuntos(draftFiles, pegadas, MAX_ADJUNTOS_PANEL);
                      setDraftFiles(r.archivos);
                      setDraftFilesAviso(r.aviso);
                    }}
                    rows={3}
                    placeholder={draftVisibility === 'internal'
                      ? 'Nota para el equipo. El usuario no la verá…'
                      : 'Respuesta que verá el usuario…'}
                  />
                  {/* Lo que sale con el mensaje. En una nota interna es tan
                      interno como ella: no llega ni al portal ni al correo. */}
                  <AdjuntosPendientes
                    archivos={draftFiles}
                    aviso={draftFilesAviso}
                    onQuitar={(i) => {
                      setDraftFiles((cur) => cur.filter((_, j) => j !== i));
                      setDraftFilesAviso(null);
                    }}
                  />
                  <div className={styles.composerRow}>
                    <BotonAdjuntar
                      disabled={sending}
                      onElegir={(nuevos) => {
                        const r = sumarAdjuntos(draftFiles, nuevos, MAX_ADJUNTOS_PANEL);
                        setDraftFiles(r.archivos);
                        setDraftFilesAviso(r.aviso);
                      }}
                    />
                    {/* Paso 51 (K66): las respuestas predefinidas son las
                        preguntas frecuentes del asistente del portal. Solo en
                        la respuesta al usuario: es texto escrito para él. */}
                    {draftVisibility === 'public' && canVerFaqs && (
                      <FaqPicker
                        token={token}
                        textoTicket={`${selected.title}\n${selected.description ?? ''}`}
                        onInsert={(t) => setDraft((d) => (d.trim() ? `${d.trimEnd()}\n\n${t}` : t))}
                      />
                    )}
                    {draftVisibility === 'public' && (
                      <label className={styles.composerCheck}>
                        <input
                          type="checkbox"
                          checked={draftResolutory}
                          onChange={(e) => setDraftResolutory(e.target.checked)}
                        />
                        Esta respuesta resuelve el ticket
                      </label>
                    )}
                    {/* Paso 93 (D1): al escribir es cuando se sabe cuánto ha
                        llevado. Opcional, y va en el mismo envío. */}
                    {canLogTime && (
                      <label className={styles.composerTime} title="Opcional. Por ejemplo: 25, 1 h, 1h30">
                        <span>Tiempo</span>
                        <input
                          className={styles.composerTimeInput}
                          value={draftMinutes}
                          onChange={(e) => setDraftMinutes(e.target.value)}
                          placeholder="25 min"
                          aria-label="Tiempo dedicado (opcional)"
                        />
                      </label>
                    )}
                    {/* Ámbar cuando es nota, tinta cuando es respuesta: el botón
                        repite de qué lado del muro estás justo en el momento
                        de pulsar (v3, paso 43). Blanco sobre #8A5A16 = 5.91:1. */}
                    <button
                      type="button"
                      className={`${styles.actionButton} ${draftVisibility === 'internal' ? styles.actionButtonNote : ''}`}
                      onClick={handleSendMessage}
                      disabled={sending || (!draft.trim() && draftFiles.length === 0)}
                    >
                      {sending ? 'Guardando…' : draftVisibility === 'internal' ? 'Guardar nota' : 'Enviar respuesta'}
                    </button>
                  </div>
                </div>
              )}
            </DetailSection>

            {message && <div className={styles.msgWrap}><p className={styles.message}>{message}</p></div>}
            </div>
            </div>
          </>
        )}
      </div>
    </div>

    {visor !== null && selected && imagenes.length > 0 && (
      <VisorImagenes
        incidenciaId={selected.id}
        token={token}
        imagenes={imagenes}
        indice={visor}
        onIndice={setVisor}
        onCerrar={() => setVisor(null)}
      />
    )}
    {dialog}
    </div>
  );
}
